import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import {
	Integration,
	IntegrationInterval,
	IntegrationProvider,
	SaveIntegrationInput,
} from '@app/model/integration-settings/Integration';
import IntegrationSettingsService from '@app/model/integration-settings/service/IntegrationSettingsService';
import { useCatalogueRefreshing, useRefreshIntegrationRooms, useSetIntegrationEnabled, useStoreIntegration } from '@app/model/integration-settings/service/IntegrationSettingsHooks';
import { TYPE_LABEL } from '../IntegrationSettings.utilities';

export type IntegrationFormValues = {
	provider: string,
	hotelCode: string,
	username: string,
	credential: string,
	credentialMask: string,
	monthsAhead: string,
	historyMonthsBack: string,
	sourceTimezone: string,
	interval: string,
	startAt: string,
};

const valuesOf = (integration: Integration): IntegrationFormValues => ({
	provider: integration.provider ?? '',
	hotelCode: integration.hotelCode ?? '',
	username: integration.username ?? '',
	credential: '',
	credentialMask: '••••••••',
	monthsAhead: integration.monthsAhead === null ? '' : String(integration.monthsAhead),
	historyMonthsBack: integration.historyMonthsBack === null ? '' : String(integration.historyMonthsBack),
	sourceTimezone: integration.sourceTimezone ?? '',
	interval: integration.interval ?? '',
	startAt: integration.startAt ?? '',
});

const toInput = (integration: Integration, values: IntegrationFormValues): SaveIntegrationInput => ({
	companyHotelId: integration.companyHotelId,
	type: integration.type,
	provider: values.provider as IntegrationProvider,
	hotelCode: values.hotelCode === '' ? null : values.hotelCode,
	username: values.username === '' ? null : values.username,
	credential: values.credential === '' ? null : values.credential,
	monthsAhead: values.monthsAhead === '' ? null : Number(values.monthsAhead),
	historyMonthsBack: values.historyMonthsBack === '' ? null : Number(values.historyMonthsBack),
	sourceTimezone: integration.type === 'RESERVATIONS' && values.sourceTimezone !== '' ? values.sourceTimezone : null,
	interval: values.interval === '' ? null : values.interval as IntegrationInterval,
	startAt: values.startAt === '' ? null : values.startAt,
});

// The form, the save, the connection test and the switch of one Integration card.
const useIntegrationCard = (integration: Integration) => {
	const toaster = useToaster();
	const store = useStoreIntegration();
	const title = `${TYPE_LABEL[integration.type]} integration`;
	const form = useForm<IntegrationFormValues>({ defaultValues: valuesOf(integration) });
	const [isReplacing, setIsReplacing] = useState<boolean>(false);

	const save = useMutation({
		mutationFn: IntegrationSettingsService.saveIntegration,
		onSuccess: (saved: Integration) => {
			store(saved);
			form.reset(valuesOf(saved));
			setIsReplacing(false);
			toaster.success(title, saved.catalogue.ready ? 'RoomPulse saved the settings.' : 'Settings saved. Fetch the room catalogue, then switch the integration on.');
		},
		onError: (error: string) => toaster.error(title, error),
	});
	const test = useMutation({
		mutationFn: IntegrationSettingsService.testIntegrationConnection,
		onSuccess: result => result.success
			? toaster.success(title, 'The connection works.')
			: toaster.error(title, result.exception),
		onError: (error: string) => toaster.error(title, error),
	});
	const refreshRooms = useRefreshIntegrationRooms(integration, error => toaster.error(title, error));
    const catalogueRefreshing = useCatalogueRefreshing(integration);
	const toggle = useSetIntegrationEnabled(error => toaster.error(title, error));

	return {
		form,
		isReplacing,
		setIsReplacing,
		isBusy: save.isPending || test.isPending || toggle.isPending || catalogueRefreshing,
        refreshRooms,
        catalogueRefreshing,
		onSave: form.handleSubmit(values => save.mutate(toInput(integration, values))),
		onTest: form.handleSubmit(values => test.mutate(toInput(integration, values))),
		onToggle: (enabled: boolean) => toggle.mutate({ integrationId: integration.id, enabled }),
	};
};

export default useIntegrationCard;
